import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { Loader2 } from "lucide-react";
import Button from "./Button";
import cn from "classnames";
import { useState, useCallback } from "react";

type ToastVariant = "success" | "error" | "loading" | "regular";
type ToastSize = "md" | "sm";

interface CustomToastProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  onClose?: () => void;
  toastId?: string;
  showCloseButton?: boolean;
  variant?: ToastVariant;
  size?: ToastSize;
}

const variantStyles: Record<ToastVariant, string> = {
  success: "text-green-500",
  error: "text-red-500",
  loading: "text-blue-500",
  regular: "",
};

const sizeStyles: Record<ToastSize, string> = {
  md: "max-w-md",
  sm: "max-w-[280px]",
};

const VariantIcon = ({ variant }: { variant: ToastVariant }) => {
  switch (variant) {
    case "success":
      return <CheckCircleIcon className="h-6 w-6" />;
    case "error":
      return <XCircleIcon className="h-6 w-6" />;
    case "loading":
      return <Loader2 className="h-6 w-6 animate-spin" />;
    default:
      return null;
  }
};

const whileClosing = (el: HTMLElement, done: () => void) => {
  const onAnimationEnd = () => {
    el.removeEventListener("animationend", onAnimationEnd);
    done();
  };
  el.addEventListener("animationend", onAnimationEnd);
  el.setAttribute("data-state", "closed");
};

const CustomToast = ({
  title,
  description,
  actionLabel,
  onAction,
  onClose,
  showCloseButton = false,
  variant = "regular",
  size = "sm",
}: CustomToastProps) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (onClose && !isClosing) {
      setIsClosing(true);
      const element = document.getElementById("toast-container");
      if (element) {
        whileClosing(element, () => {
          setIsClosing(false);
          onClose();
        });
      } else {
        onClose();
      }
    }
  }, [onClose, isClosing]);

  return (
    <div
      id="toast-container"
      className={cn(
        sizeStyles[size],
        "w-full bg-base-100 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-base-content/10",
        "animate-[enter_0.2s_ease-out]", // Add enter animation
        "data-[state=closed]:animate-[leave_0.2s_ease-in_forwards]" // Add leave animation
      )}
    >
      <div className="flex-1 w-0 p-4">
        <div className="flex items-start">
          <div className="ml-3 flex-1 items-center text-center">
            <div className="flex items-center gap-2 justify-center">
              <span className={variantStyles[variant]}>
                <VariantIcon variant={variant} />
              </span>
              <p className="text-sm font-medium text-base-content">{title}</p>
            </div>
            {description && (
              <p className="mt-1 text-sm text-base-content/60">{description}</p>
            )}
            {actionLabel && onAction && (
              <Button
                variant="primary"
                className="mt-2 w-full"
                size="sm"
                onClick={onAction}
              >
                {actionLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
      {showCloseButton && onClose && (
        <div className="flex border-l border-base-300">
          <button
            onClick={handleClose}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-blue-600 dark:text-blue-300 hover:text-blue-500 dark:hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomToast;
